/**
 * Several accounts per engine (spec 2026-10-01). A person connects up to five accounts of one
 * engine; each is a *slot*, its own CLAUDE_CONFIG_DIR on their box holding that account's sign-in.
 * The server picks the slot for every turn and names it on the wire; the box never picks.
 *
 * The first slot is the literal 'primary' (today's config dir, never moved); every other slot id
 * is a server-minted UUID v4. An absent slot id on the wire means 'primary', so an old box talking
 * to a new server, and a new box talking to an old server, keep working as one account.
 */
import { z } from 'zod';
import type { PlanEngine, StandingStatus, StandingWindow } from './standing.js';

export const PRIMARY_SLOT = 'primary' as const;
export const MAX_ACCOUNTS_PER_ENGINE = 5;
/**
 * The AGENT_READY engine feature a runner declares when it runs that engine's turns on the slot it
 * is told (engines T3: one per plan engine). 'claude_code_slots' is the string every box declares today.
 */
export const engineSlotsFeature = (engine: PlanEngine): string => `${engine}_slots`;
export const ENGINE_FEATURE_CLAUDE_CODE_SLOTS = engineSlotsFeature('claude_code');
/** A slot id on the wire: 'primary' or a server-minted UUID. Absent means PRIMARY_SLOT. */
export const SlotIdSchema = z.string().min(1).max(64);
export function slotOf(v: string | undefined | null): string { return v && v.length > 0 ? v : PRIMARY_SLOT; }

/**
 * The email of the account a slot is, when there is one to name (an absent slot is the primary).
 * The one lookup for every line that names the account a task runs on: the task's chip (web) and
 * the plan heads-up (the relay).
 */
export function slotEmail(slotId: string | undefined | null, accounts: readonly { slotId: string; email: string | null }[]): string | null {
  const slot = slotOf(slotId);
  return accounts.find((a) => a.slotId === slot)?.email || null;
}

/**
 * The person has several accounts: more than one connected. The same count the plan_limit card's
 * `accountCount` carries; one account (or none) keeps every line as it was.
 */
export function hasSeveralAccounts(accounts: readonly { status: string | null }[]): boolean {
  return accounts.filter((a) => a.status === 'connected').length > 1;
}

/**
 * Which account Bash uses first, when the person has several (Settings → Claude). 'soonest': the
 * one whose weekly allowance refills soonest, so less of the plan goes to waste (the person's order
 * breaks ties). 'my_order': the first in the person's order. Nothing stored is 'soonest', for
 * everyone. Keep in lockstep with user_preferences_engine_account_order_check.
 */
export const ENGINE_ACCOUNT_ORDERS = ['soonest', 'my_order'] as const;
export type EngineAccountOrder = (typeof ENGINE_ACCOUNT_ORDERS)[number];
export const DEFAULT_ENGINE_ACCOUNT_ORDER: EngineAccountOrder = 'soonest';
export function isEngineAccountOrder(v: unknown): v is EngineAccountOrder {
  return typeof v === 'string' && (ENGINE_ACCOUNT_ORDERS as readonly string[]).includes(v);
}

/** One connected account as Settings shows it. */
export interface EngineAccountView {
  slotId: string;
  position: number;
  email: string | null;
  orgName: string | null;
  status: 'connected' | 'needs_login' | 'error' | null;
  tier: string | null;
  plan: { tier: string | null; windows: StandingWindow[]; standingStatus: StandingStatus; measuredAt: string | null } | null;
  /** True for the account the next turn would use (pickSlot's answer). */
  inUse: boolean;
  /**
   * True for the account "Use this one now" pinned, kept while it is paused on its five-hour window
   * (it is in use again once the pause ends). Absent from a server that does not say.
   */
  pinned?: boolean;
}
